import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";

/**
 * Scene · Q1 So Far · big bold hero opener.
 *
 * Sits between Section 1's pivot ("And yet…") and the journey timeline
 * (connected-suite). Sets the frame for everything Section 2 is about to
 * walk through.
 */

const GREEN = "#96C11F";
const AQUA = "#00deb6";
const GOLD = "#e9a820";

export default function SceneQ1SoFar() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-q1-so-far">
      {/* Slow ambient breath — green → aqua → gold */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 30% 50%, rgba(150,193,31,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 70% 50%, rgba(0,222,182,0.22) 0%, transparent 55%)",
            "radial-gradient(ellipse at 50% 50%, rgba(233,168,32,0.22) 0%, transparent 55%)",
            "radial-gradient(ellipse at 30% 50%, rgba(150,193,31,0.18) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Subtle grain */}
      <div className="absolute inset-0 grain pointer-events-none opacity-30" />

      <div className="relative max-w-[1500px] mx-auto w-full text-center flex flex-col items-center">
        {/* Tiny anchor pill — calm, doesn't compete with the hero */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="inline-flex items-center gap-3 rounded-full px-5 py-2"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(150,193,31,0.45)",
          }}
          data-testid="q1-anchor-pill"
        >
          <motion.span
            className="w-2 h-2 rounded-full"
            style={{ background: GREEN, boxShadow: `0 0 14px ${GREEN}` }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          />
          <span
            className="text-[11px] tracking-[0.32em] uppercase"
            style={{ color: "rgba(255,255,255,0.85)", fontWeight: 700 }}
          >
            Where we are
          </span>
        </motion.div>

        {/* HERO — "Q1 so far." (massive, gradient) */}
        <motion.h2
          initial={{ opacity: 0, y: 32, filter: "blur(20px)", scale: 0.92 }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)", scale: 1 }}
          transition={{ duration: 1.6, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display leading-[0.92] mt-12 pb-2"
          style={{
            fontSize: "clamp(110px, 16vw, 280px)",
            fontWeight: 900,
            letterSpacing: "-0.05em",
          }}
          data-testid="q1-hero"
        >
          <span
            style={{
              background: `linear-gradient(135deg, ${GREEN} 0%, ${AQUA} 50%, ${GOLD} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: `0 0 80px ${GREEN}55`,
            }}
          >
            Q1
          </span>{" "}
          <span style={{ color: "#e2e8f0" }}>so far.</span>
        </motion.h2>

        {/* Sub-anchor */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 1.8 }}
          className="mt-12 inline-flex items-center gap-4"
          data-testid="q1-subanchor"
        >
          <span
            className="block w-12 h-px"
            style={{ background: GREEN }}
          />
          <span
            className="text-sm tracking-[0.32em] uppercase"
            style={{ color: "rgba(255,255,255,0.7)", fontWeight: 700 }}
          >
            From idea · to live · in one quarter
          </span>
          <span
            className="block w-12 h-px"
            style={{ background: GOLD }}
          />
        </motion.div>
      </div>
    </SceneShell>
  );
}
